import { Controller } from "@hotwired/stimulus"

// The slides of a flash info on its one page (29/10/2026): add, remove,
// replace by a template, and the story shown beside them as a phone shows it
// — the slide being edited, its colour, its text, its photo or video.
// New slides are cloned from a server-rendered <template>, so TinyMCE, the
// file preview and the duration check start on them through Stimulus.
export default class extends Controller {
  static targets = [
    "slidesContainer", "slide", "template", "mediaField",
    "phone", "bars", "previewImage", "previewVideo", "previewText", "previewCaption",
  ]
  static values = { label: String }

  connect() {
    this.current = this.visibleSlides()[0]
    this.render()
    // TinyMCE edits in its own frame: what is typed there is read on a beat.
    this.beat = setInterval(() => this.render(), 1500)
  }

  disconnect() {
    clearInterval(this.beat)
    this.revoke()
  }

  addSlide() {
    const card = this.cloneSlide(`${Date.now()}`)
    this.reindexSlides()
    this.select(card)
  }

  removeSlide(event) {
    const slide = event.target.closest('[data-slide-editor-target="slide"]')
    if (!slide) return
    this.discard(slide)
    this.reindexSlides()
    this.select(this.visibleSlides()[0])
  }

  // The template's slides take the place of the current ones: a saved slide
  // is marked to be destroyed, a new one is removed.
  applyTemplate(event) {
    const slides = event.detail?.slides || []
    if (!slides.length) return
    this.visibleSlides().forEach((slide) => this.discard(slide))
    slides.forEach((slide, index) => {
      const card = this.cloneSlide(`${Date.now()}${index}`)
      this.fill(card, "slide_type", slide.slide_type)
      this.fill(card, "duration_seconds", slide.duration_seconds)
      this.fill(card, "body", slide.body)
      card.querySelector('select[name*="[slide_type]"]')?.dispatchEvent(new Event("change", { bubbles: true }))
      this.fill(card, "duration_seconds", slide.duration_seconds)
    })
    this.reindexSlides()
    this.select(this.visibleSlides()[0])
  }

  slideTypeChanged(event) {
    const type = event.target.value
    const slide = event.target.closest('[data-slide-editor-target="slide"]')
    const mediaField = slide?.querySelector('[data-slide-editor-target="mediaField"]')
    if (mediaField) mediaField.classList.toggle("hidden", type === "text_only")
    // A video lasts up to 30 s; the other slides 5 s.
    const duration = slide?.querySelector('input[name*="[duration_seconds]"]')
    if (duration) duration.value = type === "video" ? 30 : 5
  }

  // The slide clicked or focused becomes the one shown.
  track(event) {
    const slide = event.target.closest('[data-slide-editor-target="slide"]')
    if (slide && slide !== this.current) this.select(slide)
    else this.render()
  }

  refresh(event) {
    if (event?.target?.type === "file") this.revoke()
    this.render()
  }

  // ── Slides ─────────────────────────────────────────────────

  cloneSlide(key) {
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, key)
    this.slidesContainerTarget.insertAdjacentHTML("beforeend", html)
    return this.slidesContainerTarget.lastElementChild
  }

  discard(slide) {
    const destroy = slide.querySelector('input[name*="_destroy"]')
    const id = slide.querySelector('input[name*="[id]"]')
    if (slide.dataset.newRecord === "true" || !id) {
      slide.remove()
      return
    }
    if (destroy) {
      destroy.value = "1"
    } else {
      const input = document.createElement("input")
      Object.assign(input, { type: "hidden", name: id.name.replace("[id]", "[_destroy]"), value: "1" })
      slide.appendChild(input)
    }
    slide.classList.add("hidden")
    slide.dataset.discarded = "true"
  }

  fill(card, field, value) {
    if (value === undefined || value === null) return
    const element = card.querySelector(
      `select[name*="[${field}]"], input[name*="[${field}]"], textarea[name*="[${field}]"]`,
    )
    if (element) element.value = value
  }

  visibleSlides() {
    return this.slideTargets.filter((slide) => slide.dataset.discarded !== "true")
  }

  reindexSlides() {
    this.visibleSlides().forEach((slide, index) => {
      slide.dataset.index = index
      const title = slide.querySelector("h3")
      if (title && this.hasLabelValue) title.textContent = this.labelValue.replace("%{number}", index + 1)
      const position = slide.querySelector('input[name*="[position]"]')
      if (position) position.value = index
    })
  }

  select(slide) {
    this.current = slide
    this.slideTargets.forEach((card) => card.classList.toggle("is-current", card === slide))
    this.render()
  }

  // ── Preview ────────────────────────────────────────────────

  render() {
    if (!this.hasPhoneTarget) return
    const slides = this.visibleSlides()
    if (!slides.includes(this.current)) this.current = slides[0]
    const slide = this.current
    const position = slides.indexOf(slide)
    this.barsTarget.replaceChildren(...slides.map((_, index) => {
      const bar = document.createElement("span")
      bar.className = index <= position ? "is-done" : ""
      return bar
    }))
    if (!slide) return

    const color = slide.querySelector('input[name*="[background_color]"]')?.value
    this.phoneTarget.style.backgroundColor = color || ""
    this.previewTextTarget.textContent = this.bodyOf(slide)
    this.showMedia(slide)
    if (this.hasPreviewCaptionTarget && this.hasLabelValue) {
      this.previewCaptionTarget.textContent = this.labelValue.replace("%{number}", position + 1)
    }
  }

  bodyOf(slide) {
    const area = slide.querySelector('textarea[name*="[body]"]')
    if (!area) return ""
    const editor = window.tinymce?.get(area.id)
    if (editor) return editor.getContent({ format: "text" }).trim()
    const scratch = document.createElement("div")
    scratch.innerHTML = area.value
    return scratch.textContent.trim()
  }

  // The file just picked, or the one already saved; nothing for a text slide.
  showMedia(slide) {
    const type = slide.querySelector('select[name*="[slide_type]"]')?.value
    const file = slide.querySelector('input[type="file"][name*="[media]"]')?.files?.[0]
    let url = slide.dataset.mediaUrl || ""
    let kind = slide.dataset.mediaKind || "image"
    if (file) {
      if (!this.objectUrl || this.objectFile !== file) {
        this.revoke()
        this.objectUrl = URL.createObjectURL(file)
        this.objectFile = file
      }
      url = this.objectUrl
      kind = file.type.startsWith("video") ? "video" : "image"
    }
    const shown = type !== "text_only" && type !== "audio" && url
    this.toggleMedia(this.previewImageTarget, shown && kind === "image" ? url : null)
    this.toggleMedia(this.previewVideoTarget, shown && kind === "video" ? url : null)
  }

  toggleMedia(element, url) {
    element.hidden = !url
    if (url && element.getAttribute("src") !== url) element.setAttribute("src", url)
    if (!url) element.removeAttribute("src")
  }

  revoke() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
    this.objectFile = null
  }
}
